import angular from 'angular';

import {
    DigitalPaymentHandler,
    DigitalPaymentHandlerOptions 
} from 'src/app/shared/model/digital-payments.model';

import {
    DocumentPrintHook,
    DocumentPrinterOptions
} from 'src/app/shared/model/document-printer.model';

import {
    BeddyApiService,
    BedzzleCommonService ,
} from 'src/app/features/digital-payments';

import { validate as validateUuid } from 'uuid';

import {
    ConfigurationManagerService,
    EntityManagerService,
    UserActiveSessionManagerService
} from 'src/app/core';

import { Sales } from 'tilby-models';
import { BeddyPaymentsService } from 'app/modules/cashregister/service/dialog/beddy-payments/beddy-payments';

export class BeddyService implements DigitalPaymentHandler, DocumentPrintHook {

    constructor(
        private configurationManagerService: ConfigurationManagerService,
        private beddyPayments: BeddyPaymentsService,
        private beddyApi: BeddyApiService,
        private bedzzleCommon: BedzzleCommonService,
        private entityManagerService: EntityManagerService,
        private userActiveSession: UserActiveSessionManagerService
    ) {
    }

    public async payment (amount: number, options: DigitalPaymentHandlerOptions) {
        const currentSale = options.sale;
        let folio;

        try {
            folio = await this.beddyPayments.show({ amount: amount, paymentMethodId: options.paymentMethod.id });
        } catch(error) {
            throw (typeof error === 'object' ? 'CANCELED' : error);
        }

        if (!folio) {
            throw 'MISSING_FOLIO';
        }

        const transaction = await this.bedzzleCommon.buildTransaction(folio, currentSale);

        try {
            const response = await this.beddyApi.createTransaction(options.paymentMethod.id, transaction);

            if(response.data.folioId) {
                folio.folioId = response.data.folioId;
                transaction.folioId = response.data.folioId;
            }

            let responseString = '';

            try {
                responseString = JSON.stringify(transaction);
            } catch(err) {
                //Nothing to do
            } finally {
                return {
                    acquirer_name: 'Beddy',
                    payment_data: responseString,
                    tail: this.bedzzleCommon.buildTail(folio, currentSale, 'beddy'),
                    unclaimed: true
                };
            }
        } catch(error: any) {
            switch (error.status) {
                case -1:
                    throw "BEDDY_OFFLINE";
                case 400:
                case 401:
                    throw "BEDDY_UNABLE_TO_COMPLETE_TRANSACTION";
                default:
                    throw "UNKNOWN_ERROR";
            }
        }
    }

    private async performRefund(sale: Sales) {
        if (!sale) {
            return;
        }

        //Make sure that the referenced sale had a bedzzle payment and in that case perform the rollback
        const parentSaleId = sale.sale_parent_id || sale.sale_parent_uuid;

        if(!parentSaleId) {
            return;
        }

        const parentSale = validateUuid(parentSaleId.toString())
            ? await this.entityManagerService.sales.fetchCollectionOnline({ uuid: parentSaleId }).then((sales) => (sales as Sales[])[0])
            : await this.entityManagerService.sales.fetchOneOfflineFirst(parentSaleId);

        if(!parentSale) {
            return;
        }

        const bedzzlePayment = parentSale.payments?.find((payment) => payment.payment_method_type_id === 23);

        if(!bedzzlePayment) {
            return;
        }

        const paymentMethod = await this.entityManagerService.paymentMethods.fetchOneOffline(bedzzlePayment.payment_method_id);

        if(!paymentMethod) {
            return;
        }

        const shop = this.userActiveSession.getSession()?.shop;

        try {
            return await this.beddyApi.rollbackTransaction(paymentMethod.id, String(shop?.id), parentSale.uuid!);
        } catch(err: any) {
            return err?.data?.error?.message;
        }
    }

    public async refund (amount: number, options: DigitalPaymentHandlerOptions) {
        const currentSale = options.sale;
        const refundResult = await this.performRefund(currentSale);

        return {
            acquirer_name: 'Beddy',
            payment_data: JSON.stringify(refundResult),
            unclaimed: true
        };
    }

    public isEnabled() {
        return true;
    }

    public async printFailHook (sale: Sales, printerDocumentData: DocumentPrinterOptions) {
        const beddyPayment = sale.payments?.find((payment) => payment.payment_method_type_id == 23);
        
        if(!beddyPayment) {
            return;
        }

        const paymentMethod = await this.entityManagerService.paymentMethods.fetchOneOffline(beddyPayment.payment_method_id);

        if(!paymentMethod) {
            return;
        }
        
        let result = 'KO';
        
        try {
            const shop = this.userActiveSession.getSession()?.shop;
            await this.beddyApi.rollbackTransaction(paymentMethod.id, String(shop?.id), sale.uuid!);
            result = 'OK';
        } catch (err) {
            //Nothing to do
        } finally {
            throw `BEDDY_ROLLBACK_${result}`;
        }
    }
    
    public async postPrintHook (sale: Sales, printerDocumentData: DocumentPrinterOptions) {
        /*
            1. Sending other sales to Beddy must be enabled
            2. The sale must not have a Beddy payment
        */
        if(
            !this.configurationManagerService.getPreference('zucchetti_pms.send_other_sales') ||
            sale.payments?.some((payment) => payment.payment_method_type_id == 23)
        ) {
            return;
        }

        //TODO: allow selection of payment?
        const beddyPayment = await this.entityManagerService.paymentMethods.fetchCollectionOffline({ payment_method_type_id: 23 }).then((methods) => methods[0]);

        if(!beddyPayment) {
            return;
        }

        this.bedzzleCommon.buildExtTransaction({ folioId: null }, sale).then((transaction) => this.beddyApi.createTransaction(beddyPayment.id, transaction));
    }
}

BeddyService.$inject = [
    "checkManager",
    "beddyPayments",
    "beddyApi",
    "bedzzleCommon",
    "entityManager",
    "userActiveSession"
];

angular.module('digitalPayments').service('beddy', BeddyService);